/**
 * ChatBox object-creation sink (typed object create + local message + invalidation).
 *
 * Shared sink for the four `/task|/note|/contact|/event` create commands plus
 * `handleModuleSelection`, so the external signature (the five positional args)
 * MUST stay identical.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { queryKeys as QueryKeys } from '@/lib/query/keys';
import type { api as Api } from '@/lib/api';
import type { Conversation } from '@/types/conversations';

/**
 * Signature of the `addLocalMessages` callback surfaced by `useChat`
 * (see `useChat.types.ts` → `UseChatReturn.addLocalMessages`). Reproduced here
 * — matching `chatbox/slash-commands.ts` — so the extracted sink stays fully
 * typed and ChatBox's typed callback assigns to it without widening.
 */
type AddLocalMessages = (
  userContent: string,
  assistantContent: string,
  conversationId?: string,
  metadata?: {
    routing?: string;
    durationMs?: number;
    intent?: string;
  },
) => {
  userMessageId?: string;
  assistantMessageId: string;
};

export interface CompleteObjectCreationDeps {
  token: string | null;
  activeConversation: Conversation | null;
  addLocalMessages: AddLocalMessages;
  queryClient: QueryClient;
  queryKeys: typeof QueryKeys;
  api: typeof Api;
}

export async function completeObjectCreation(
  objectType: 'task' | 'note' | 'contact' | 'event',
  parsedData: any,
  moduleId: string,
  moduleName: string,
  originalInput: string,
  deps: CompleteObjectCreationDeps,
): Promise<void> {
  const { token, activeConversation, addLocalMessages, queryClient, queryKeys, api } = deps;

  const startTime = Date.now();

  try {
    let objectData: any;
    let emoji: string;
    let displayTitle: string;

    switch (objectType) {
      case 'task':
        objectData = {
          type: 'task',
          title: parsedData.title,
          dueAt: parsedData.dueAt,
          priority: parsedData.priority,
          status: 'todo',
          moduleIds: [moduleId],
        };
        emoji = '☐';
        displayTitle = parsedData.title;
        break;
      case 'note':
        objectData = {
          type: 'note',
          title: parsedData.title,
          body: parsedData.content || '',
          moduleIds: [moduleId],
        };
        emoji = '📝';
        displayTitle = parsedData.title;
        break;
      case 'contact':
        objectData = {
          type: 'contact',
          name: parsedData.name,
          emails: parsedData.emails,
          phones: parsedData.phones,
          moduleIds: [moduleId],
        };
        emoji = '👤';
        displayTitle = parsedData.name;
        break;
      case 'event':
        objectData = {
          type: 'event',
          title: parsedData.title,
          startAt: parsedData.startAt,
          endAt: parsedData.endAt,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          moduleIds: [moduleId],
        };
        emoji = '📅';
        displayTitle = parsedData.title;
        break;
    }

    if (activeConversation?.id) {
      await api.objects.createConversationObject(token, activeConversation.id, objectData);
    } else {
      await api.objects.createObject(token, objectData);
    }

    const durationMs = Date.now() - startTime;

    const response = `**${objectType.charAt(0).toUpperCase() + objectType.slice(1)} created in ${moduleName}**\n\n${emoji} ${displayTitle}`;

    addLocalMessages(`/${objectType} ${originalInput}`, response, activeConversation?.id, {
      routing: `command:${objectType}`,
      durationMs,
    });

    queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType(objectType) });
    if (activeConversation?.id) {
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.objects(activeConversation.id) });
    }
  } catch (error) {
    const durationMs = Date.now() - startTime;
    addLocalMessages(`/${objectType} ${originalInput}`, `Failed to create ${objectType}: ${error instanceof Error ? error.message : 'Unknown error'}`, activeConversation?.id, {
      routing: `command:${objectType}`,
      durationMs,
    });
  }
}
